import { useVirtualizer } from '@tanstack/react-virtual';
import type { ComponentProps } from 'react';
import { useEffect, useRef } from 'react';

import { cn } from '../../lib/utils';
import { Checkbox } from '../../primitives/checkbox';
import { Text } from '../../primitives/text';

export interface CheckboxListOption {
  value: string;
  label: string;
}

type CheckboxListProps = Omit<ComponentProps<'div'>, 'onChange'> & {
  options: CheckboxListOption[];
  /** Selected value ids. Controlled — the component never owns selection state. */
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** Max rendered height (px) before the list scrolls. */
  maxHeight?: number;
  /** Fixed row height (px). Keep deterministic for stable snapshots. */
  rowHeight?: number;
  emptyLabel?: string;
  /**
   * When this value changes the list scrolls back to the top. Pass a value that
   * changes whenever the option set is re-filtered (e.g. a search query) so newly
   * matching rows aren't hidden below a stale scroll position.
   */
  scrollResetKey?: string | number;
};

const DEFAULT_MAX_HEIGHT = 240;
const DEFAULT_ROW_HEIGHT = 32;
const EMPTY_STATE_HEIGHT = 32;

/**
 * Virtualised, controlled multi-checkbox list. Renders only the visible rows via
 * `@tanstack/react-virtual` so it stays performant for large option sets (e.g. all
 * org users) without mounting N checkboxes at once.
 */
export const CheckboxList = ({
  options,
  value,
  onChange,
  disabled = false,
  maxHeight = DEFAULT_MAX_HEIGHT,
  rowHeight = DEFAULT_ROW_HEIGHT,
  emptyLabel = 'No options',
  scrollResetKey,
  className,
  style,
  ...props
}: CheckboxListProps) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: options.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => rowHeight,
    overscan: 8,
  });

  // Reset the scroll position to the top whenever `scrollResetKey` changes. A
  // virtualised list keeps its `scrollTop` across data changes, so after the
  // option set is filtered the matches can render below the fold; scrolling back
  // to the top keeps them visible. The virtualizer instance is stable, so this
  // only fires on mount and on key changes.
  useEffect(() => {
    virtualizer.scrollToOffset(0);
  }, [scrollResetKey, virtualizer]);

  const toggle = (optionValue: string) => {
    onChange(
      value.includes(optionValue)
        ? value.filter((v) => v !== optionValue)
        : [...value, optionValue]
    );
  };

  const hasOptions = options.length > 0;
  // Drive an explicit, transitionable height so the list grows/shrinks smoothly
  // when the option set changes (e.g. a SearchableCheckboxList narrowing the
  // results) — a content-driven `auto` height can't be animated. Capped at
  // maxHeight; scrolls beyond.
  const listHeight = hasOptions
    ? Math.min(maxHeight, virtualizer.getTotalSize())
    : EMPTY_STATE_HEIGHT;

  return (
    <div
      ref={scrollRef}
      data-slot={'checkbox-list'}
      role={'group'}
      className={cn(
        'overflow-y-auto transition-[height] duration-200 ease-out motion-reduce:transition-none',
        className
      )}
      style={{ ...style, height: listHeight }}
      {...props}
    >
      {hasOptions ? (
        <div
          style={{
            height: virtualizer.getTotalSize(),
            position: 'relative',
            width: '100%',
          }}
        >
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const option = options[virtualRow.index];

            return (
              <label
                key={option.value}
                className={cn(
                  'absolute left-0 top-0 flex w-full items-center gap-2 rounded pl-1.5 text-base cursor-pointer'
                )}
                style={{
                  height: virtualRow.size,
                  transform: `translateY(${virtualRow.start}px)`,
                }}
              >
                <Checkbox
                  size={'md'}
                  // base-ui renders the checkbox as a button[role=checkbox]; the
                  // wrapping <label> names the hidden input, not the button — so
                  // the button needs its own accessible name.
                  aria-label={option.label}
                  checked={value.includes(option.value)}
                  onCheckedChange={() => toggle(option.value)}
                  disabled={disabled}
                />
                <span className={cn('min-w-0 flex-1 truncate')}>
                  {option.label}
                </span>
              </label>
            );
          })}
        </div>
      ) : (
        <Text
          preset={'body-sm'}
          className={cn('px-1.5 py-1 text-muted-foreground')}
        >
          {emptyLabel}
        </Text>
      )}
    </div>
  );
};

export type { CheckboxListProps };
